import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { discoverBusinesses, linkMeToBusiness, myLinkedBusinesses, unlinkMyBusiness, updateMyBusinessLink } from '../../api/businessesReader'
import type { BusinessProfile, MeBusinessMembership } from '../../types/businesses'

export default function SettingsCompanies() {
  const [linked, setLinked] = useState<MeBusinessMembership[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<BusinessProfile[]>([])
  const [role, setRole] = useState<'EMPLOYEE' | 'EDITOR'>('EMPLOYEE')
  const [showPublic, setShowPublic] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [linkingSlug, setLinkingSlug] = useState<string | null>(null)

  const loadLinked = async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await myLinkedBusinesses()
      setLinked(rows)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load linked companies.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLinked()
  }, [])

  useEffect(() => {
    const q = query.trim()
    if (!q) {
      setResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    discoverBusinesses({ q, sort: 'newest', page: 1, page_size: 8 })
      .then((resp) => {
        if (!cancelled) setResults(resp.results || [])
      })
      .catch(() => {
        if (!cancelled) setResults([])
      })
      .finally(() => {
        if (!cancelled) setSearching(false)
      })
    return () => {
      cancelled = true
    }
  }, [query])

  const linkedByBusinessId = useMemo(() => {
    const map = new Map<number, MeBusinessMembership>()
    for (const row of linked) map.set(row.business.id, row)
    return map
  }, [linked])

  const onLink = async (business: BusinessProfile) => {
    if (linkingSlug) return
    setLinkingSlug(business.slug)
    setError(null)
    try {
      await linkMeToBusiness({
        business_slug: business.slug,
        role,
        is_public: showPublic,
      })
      await loadLinked()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to link company.')
    } finally {
      setLinkingSlug(null)
    }
  }

  const onTogglePublic = async (membership: MeBusinessMembership) => {
    if (busyId != null) return
    setBusyId(membership.id)
    setError(null)
    try {
      await updateMyBusinessLink(membership.id, { is_public: !membership.is_public })
      await loadLinked()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update visibility.')
    } finally {
      setBusyId(null)
    }
  }

  const onUnlink = async (membership: MeBusinessMembership) => {
    if (busyId != null) return
    const ok = window.confirm(`Unlink from ${membership.business.name}?`)
    if (!ok) return
    setBusyId(membership.id)
    setError(null)
    try {
      await unlinkMyBusiness(membership.id)
      await loadLinked()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to unlink business.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-extrabold">Companies</h1>
        <p className="mt-1 text-white/70">Link yourself to business pages and control public visibility.</p>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm font-semibold">Link company</div>
        <div className="mt-3 grid gap-2 md:grid-cols-[1fr_auto_auto]">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search business pages by name or slug"
            className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'EMPLOYEE' | 'EDITOR')}
            className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none"
          >
            <option value="EMPLOYEE">Employee</option>
            <option value="EDITOR">Editor</option>
          </select>
          <label className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm">
            <input type="checkbox" checked={showPublic} onChange={(e) => setShowPublic(e.target.checked)} />
            <span>Show publicly</span>
          </label>
        </div>
        {searching ? <div className="mt-3 text-sm text-white/70">Searching...</div> : null}
        {!searching && query.trim() && results.length === 0 ? <div className="mt-3 text-sm text-white/70">No businesses found.</div> : null}
        {!searching && results.length > 0 ? (
          <div className="mt-3 space-y-2">
            {results.map((biz) => {
              const existing = linkedByBusinessId.get(biz.id)
              return (
                <div key={biz.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                  <div>
                    <div className="text-sm font-semibold">{biz.name}</div>
                    <div className="text-xs text-white/60">/{biz.slug}</div>
                  </div>
                  {existing ? (
                    <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-200">
                      Linked
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onLink(biz)}
                      disabled={Boolean(linkingSlug)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${linkingSlug ? 'border border-white/10 bg-white/10 text-white/50' : 'bg-brand text-white hover:bg-brand-600'}`}
                    >
                      {linkingSlug === biz.slug ? 'Linking...' : 'Link'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        ) : null}
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm font-semibold">Linked companies</div>
        {loading ? <div className="mt-3 text-sm text-white/70">Loading...</div> : null}
        {!loading && linked.length === 0 ? <div className="mt-3 text-sm text-white/70">No linked companies yet.</div> : null}
        {!loading && linked.length > 0 ? (
          <div className="mt-3 space-y-2">
            {linked.map((row) => {
              const isBusy = busyId === row.id
              return (
                <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                  <div>
                    <div className="text-sm font-semibold">{row.business.name}</div>
                    <div className="text-xs text-white/60">
                      {row.role} • {row.public_status.replaceAll('_', ' ')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/b/${row.business.slug}`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">
                      View
                    </Link>
                    <button
                      type="button"
                      onClick={() => onTogglePublic(row)}
                      disabled={isBusy}
                      className={`rounded border px-2 py-1 text-xs ${isBusy ? 'border-white/10 bg-white/10 text-white/50' : 'border-white/15 bg-white/5 hover:bg-white/10'}`}
                    >
                      {row.is_public ? 'Hide publicly' : 'Show publicly'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onUnlink(row)}
                      disabled={isBusy}
                      className={`rounded border px-2 py-1 text-xs ${isBusy ? 'border-white/10 bg-white/10 text-white/50' : 'border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                    >
                      Unlink
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}
        {error ? <div className="mt-3 text-sm text-red-300">{error}</div> : null}
      </div>
    </div>
  )
}
